import {
  useRef, useState, useEffect,
  useMemo,
} from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { eachDayOfInterval, parseISO } from 'date-fns';
import { Calendar } from '../components/molecules/FullCalendar/FullCalendar';
import useFullCalendar, {
  DAY_COLUMN_SLOT_DURATION,
  DAY_COLUMN_SLOT_MAX_TIME,
  DAY_COLUMN_SLOT_MIN_TIME,
} from '../domain/fullCalendar/useFullCalendar';
import {
  useTimeline,
  useTimelineFilters,
  useTimelineFiltersMutation,
  TIMELINE_FILTERS_QUERY_KEY,
  TIMELINE_QUERY_KEY,
} from '../services/calendar/calendarQueries';
import {
  useDeleteTimelineEventOptimistic,
  useDuplicateTimelineEventOptimistic,
  useFastEditEventOptimistic,
} from '../services/calendar/calendarOptimistic';
import { normalizeUrl } from '../utils/url';
import useCalendarSidebarFilters from '../domain/calendarSidebarFilters/useCalendarSidebarFilters';
import renderCalendarEvent from '../components/molecules/CalendarEventRender/CalendarEventRender';
import CalendarHeader from '../components/molecules/FullCalendarHeader/FullCalendarHeader';
import renderCalendarResourceLabel from '../components/molecules/CalendarResourceLabelRender/CalendarResourceLabelRender';
import EventContextMenu from '../components/molecules/EventContextMenu/EventContextMenu';
import DateSelectionModal from '../components/molecules/DateSelectionModal/DateSelectionModal';
import Modal, { ModalHeader } from '../components/molecules/Modal/Modal';
import modalStyles from '../components/molecules/Modal/Modal.module.css';
import CalendarEventFocus from '../components/molecules/CalendarEventFocus/CalendarEventFocus';
import Button from '../components/atoms/Button/Button';
import useCalendarHeader, { SCOPE_OPTIONS } from '../domain/fullCalendarHeader/useFullCalendarHeader';
import FilterCalendarSidebar from '../components/organisms/FilterCalendarSidebar/FilterCalendarSidebar';
import { INITIAL_RANGE } from '../domain/dateRange/useDateRangeUseCase';
import useDateRange from '../domain/dateRange/useDateRange';
import { mapApiDateRangeToUiRange } from '../domain/globalFilters/globalFiltersUseCase';
import {
  apiMaxTimeToPickerValue,
  buildCalendarSplits,
  buildTimelineFiltersPostPayload,
  buildEventFocusViewModel,
  buildTimelineFiltersPostPayloadWithOverrides,
  formatApiTimeForFullCalendar,
  getViewTypeConfig,
  mapTimelineResourceToCalendarResource,
  mapTimelineToFullCalendarData,
  shouldShowTimelineOrientationSwitch,
  slotMinutesToDuration,
} from '../domain/fullCalendar/fullCalendarUseCase';
import formatDate from '../utils/formatDate';
import TableTitle from '../components/molecules/TableTitle/TableTitle';
import FloatingMenu, {
  FloatingMenuGroups,
  FloatingMenuGroup,
} from '../components/atoms/FloatingMenu/FloatingMenu';
import ResponsiveActionButton from '../components/molecules/ResponsiveActionButton/ResponsiveActionButton';
import {
  PopoverContent,
  PopoverTrigger,
} from '../components/atoms/Popover/Popover';
import CustomVueListGroup, {
  CustomVueListGroupList,
  CustomVueListGroupTitle,
} from '../components/organisms/CustomVueListGroup/CustomVueListGroup';
import ColumnsVisibility from '../components/molecules/ColumnsVisibility/ColumnsVisibility';
import SearchField from '../components/molecules/SearchField/SearchField';
import SearchGroupList from '../components/molecules/SearchGroupList/SearchGroupList';
import SearchListItem from '../components/molecules/SearchListItem/SearchListItem';
import LabelWithIconAndColor from '../components/molecules/LabelWithIconAndColor/LabelWithIconAndColor';
import { Popover } from '../store/popover/PopoverContext';
import { useFormPanelContext } from '../store/formPanel/FormPanelContext';
import useAddElement from '../domain/addElement/useAddElement';
import { addIdsToAddFormUrls } from '../domain/addElement/addElementUseCase';
import useStickyTopElems from '../hooks/useStickyTopElems';
import { useSyncHorizontalScroll } from '../hooks/useSyncHorizontalScroll';
import { useScrollerElements } from '../hooks/useScrollerElements';
import { useContexte } from '../hooks/useContexte';
import useCollapsibleSidebar from '../hooks/useCollapsibleSidebar';
import useToast from '../components/molecules/Toast/useToast';
import { Skeleton } from '../components/atoms/Skeleton/Skeleton';
import cn from '../utils/cn';
import styles from './Timeline.module.css';

const SIDEBAR_TRANSITION_MS = 220;

/** @type {FullCalendarData} */
const EMPTY_CALENDAR_DATA = {
  viewType: 'horizontalresourceDay',
  dateStart: '',
  dateEnd: '',
  dateFilter: '',
  minTime: '12:00',
  maxTime: '26:00',
  slotMinutes: 15,
  exportUrls: [],
  addFormUrls: [],
  resources: [],
  events: [],
  resourcesAreaColumns: [],
  viewConfig: getViewTypeConfig('horizontalresourceDay'),
};

/**
 * Loading placeholder matching the timeline sidebar + calendar layout.
 * @param {object} props
 * @param {number} props.stickyHeight
 * @param {number} props.remainingHeight
 * @param {boolean} [props.isSidebarCollapsed=false]
 * @returns {React.ReactElement}
 */
function TimelineSkeleton({ stickyHeight, remainingHeight, isSidebarCollapsed = false }) {
  return (
    <div
      className={cn([
        styles.layout,
        isSidebarCollapsed && styles['layout-sidebar-collapsed'],
      ])}
      style={/** @type {import('react').CSSProperties} */ ({
        '--sidebar-sticky-top': `${stickyHeight}px`,
        '--sidebar-remaining-height': `${remainingHeight}px`,
      })}
    >
      <div className={styles['sidebar-column']}>
        <Skeleton
          className={styles['skeleton-sidebar-block']}
          shape="rectangle"
          width="100%"
          height="100%"
          theme="table"
        />
      </div>
      <div className={styles.content}>
        <div className={styles['skeleton-content-blocks']}>
          <Skeleton
            className={styles['skeleton-content-block-top']}
            shape="rectangle"
            width="100%"
            height="100%"
            theme="table"
          />
          <Skeleton
            className={styles['skeleton-content-block-main']}
            shape="rectangle"
            width="100%"
            height="100%"
            theme="table"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Timeline view component.
 * @param {object} props
 * @param {ApiPage} [props.page] - The page configuration
 * @param {boolean} [props.isInTab=false] - Rendered under a detail page tab (hides the title)
 * @returns {React.ReactElement} The Timeline component.
 */
function Timeline({ page, isInTab = false }) {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const contexte = useContexte();
  const path = page?.path;
  const { stickyHeight, remainingHeight } = useStickyTopElems([
    'page-header',
    'banner-header-action-row',
    'banner-header-content',
    'horizontal-tabs',
  ]);
  const firstScrollDivRef = useRef(null);
  const secondScrollDivRef = useRef(null);
  const {
    isCollapsed: isSidebarCollapsed,
    onToggleClick: onToggleSidebarCollapse,
  } = useCollapsibleSidebar();

  const { data: timelineFilters, isLoading: isTimelineFiltersLoading } = useTimelineFilters(path, contexte);
  const { data: timeline, isLoading: isTimelineLoading } = useTimeline(path, timelineFilters, contexte, {
    placeholderData: keepPreviousData,
  });

  const { mutate: mutateFastEditEvent } = useFastEditEventOptimistic();
  const { mutate: mutateDeleteTimelineEvent } = useDeleteTimelineEventOptimistic();
  const { mutate: mutateDuplicateTimelineEvent } = useDuplicateTimelineEventOptimistic();

  const {
    mutate: mutateTimelineFilters,
    isPending: isTimelineFiltersMutating,
  } = useTimelineFiltersMutation(path, contexte, {
    onSuccess: (updatedFilters) => {
      queryClient.setQueryData([TIMELINE_FILTERS_QUERY_KEY, path, contexte], updatedFilters);
      queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY, path] });
    },
    onError: (error) => {
      notify({
        message: error?.message || t('toast.timelineFilters.error'),
        type: 'alert',
      });
    },
  });

  const calendarData = useMemo(() => {
    if (!timeline) {
      return EMPTY_CALENDAR_DATA;
    }
    return mapTimelineToFullCalendarData(timeline, timelineFilters);
  }, [timeline, timelineFilters]);

  const { viewConfig } = calendarData;
  const direction = timelineFilters?.direction.selected ?? 'horizontal';
  const periodType = timelineFilters?.dateRange.type ?? 'week';

  const ranges = useMemo(() => {
    if (!timelineFilters?.dateRange) {
      return [{
        startDate: new Date(),
        endDate: new Date(),
        key: INITIAL_RANGE.key,
      }];
    }
    return mapApiDateRangeToUiRange(timelineFilters.dateRange);
  }, [timelineFilters?.dateRange]);

  const calendarRefs = useRef([]);
  const headerCalendarRef = useRef(null);
  const layoutRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  useEffect(() => {
    const node = layoutRef.current;

    const update = () => {
      headerCalendarRef.current?.getApi()?.updateSize();
      calendarRefs.current.forEach((calendarRef) => calendarRef?.getApi()?.updateSize());
    };

    const timeoutId = window.setTimeout(update, SIDEBAR_TRANSITION_MS + 50);
    const onTransitionEnd = (/** @type {TransitionEvent} */ event) => {
      if (event.target !== node || event.propertyName !== 'grid-template-columns') return;
      window.clearTimeout(timeoutId);
      update();
    };

    node?.addEventListener('transitionend', onTransitionEnd);
    return () => {
      window.clearTimeout(timeoutId);
      node?.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [isSidebarCollapsed]);

  const dateRange = useDateRange({
    initialRanges: ranges,
    type: periodType,
    typeOptions: timelineFilters?.dateRange.typeOptions ?? SCOPE_OPTIONS,
    isLoading: isTimelineFiltersLoading,
  });

  const {
    startTime,
    endTime,
    normalizedSlotMinTime,
    normalizedSlotMaxTime,
    slotDuration,
    setSlotDuration,
    setStartTime,
    setEndTime,
    toggleResourceVisibility,
    toggleGroupVisibility,
    visibleResources,
    isResourceVisible,
    resourcesGroups,
    resourceGroupField,
    getGroupVisibilityState,
    showResourcesModal,
    openResourcesModal,
    closeResourcesModal,
    searchResources,
    setSearchResources,
  } = useCalendarHeader(calendarData, calendarData.resourcesAreaColumns);

  useEffect(() => {
    if (!timeline) return;
    setStartTime(timeline.minTime);
    setEndTime(apiMaxTimeToPickerValue(timeline.maxTime));
    setSlotDuration(slotMinutesToDuration(timeline.slotMinutes));
  }, [timeline, setEndTime, setSlotDuration, setStartTime]);

  const showHourHeaders = viewConfig.hourHeaders;
  // Vertical agendas have no master header: they render their own day headers
  const isAgendaView = viewConfig.fcView === 'timeGrid';
  const showStickyMasterHeader = viewConfig.splitBy === 'day'
    || (!viewConfig.splitBy && viewConfig.hourHeaders && viewConfig.fcView === 'resourceTimeline');
  const slotMinTime = viewConfig.usesSlotTimes
    ? formatApiTimeForFullCalendar(timeline?.minTime ?? startTime)
    : normalizedSlotMinTime;
  const slotMaxTime = viewConfig.usesSlotTimes
    ? formatApiTimeForFullCalendar(timeline?.maxTime ?? endTime)
    : normalizedSlotMaxTime;

  const postTimelineFiltersUpdate = (
    /** @type {Partial<ApiManagerTimelineFiltersCreate>} */ overrides,
    /** @type {import('@tanstack/react-query').MutateOptions<ApiManagerTimelineFilter, Error, ApiManagerTimelineFiltersCreate, unknown>} */ mutateOptions = {},
  ) => {
    if (!timelineFilters) return;
    mutateTimelineFilters(
      buildTimelineFiltersPostPayloadWithOverrides(timelineFilters, overrides),
      mutateOptions,
    );
  };

  const handleDateRangeMutation = (
    /** @type {ApiManagerDateRangeType} */ newType,
    /** @type {import('react-date-range').Range} */ newRange,
  ) => {
    if (!newRange || !timelineFilters) return;

    postTimelineFiltersUpdate({
      dateRange: {
        type: /** @type {ApiManagerTimelinePeriodType} */ (newType),
        selectedDate: formatDate(newRange.startDate, 'yyyy-MM-dd', 'fr'),
        rangeStartDate: formatDate(newRange.startDate, 'yyyy-MM-dd', 'fr'),
        rangeEndDate: formatDate(newRange.endDate, 'yyyy-MM-dd', 'fr'),
      },
    });

    const startDate = new Date(newRange.startDate);
    headerCalendarRef.current?.getApi()?.gotoDate(startDate);
    calendarRefs.current[0]?.getApi()?.gotoDate(startDate);
  };

  const handlePrevNextValueMutation = (/** @type {DateRangeDirection} */ navigationDirection) => {
    if (!timelineFilters) return;

    const targetDate = navigationDirection === 'minus'
      ? timelineFilters.dateRange.prevDate
      : timelineFilters.dateRange.nextDate;

    postTimelineFiltersUpdate({
      dateRange: {
        ...buildTimelineFiltersPostPayload(timelineFilters).dateRange,
        selectedDate: targetDate,
      },
    });
  };

  const handleTypeValueChangeMutation = (/** @type {ApiManagerDateRangeType} */ newType) => {
    if (!timelineFilters) return;

    postTimelineFiltersUpdate({
      dateRange: {
        ...buildTimelineFiltersPostPayload(timelineFilters).dateRange,
        type: /** @type {ApiManagerTimelinePeriodType} */ (newType),
      },
    });
  };

  const handleOrientationChange = (/** @type {'horizontal' | 'vertical'} */ newDirection) => {
    postTimelineFiltersUpdate({ direction: newDirection });
  };

  const handleStartTimeChange = (/** @type {string} */ value) => {
    setStartTime(value);
    if (!timelineFilters) return;
    postTimelineFiltersUpdate({
      timeWindow: {
        minTime: value,
        maxTime: timelineFilters.timeWindow.maxTime,
      },
    });
  };

  const handleEndTimeChange = (/** @type {string} */ value) => {
    setEndTime(value);
    if (!timelineFilters) return;
    postTimelineFiltersUpdate({
      timeWindow: {
        minTime: timelineFilters.timeWindow.minTime,
        maxTime: value,
      },
    });
  };

  const selectedResourceIds = timelineFilters?.resources?.selectedIds ?? [];

  const allResourceOptions = useMemo(
    () => (timelineFilters?.resources?.options ?? []).map(mapTimelineResourceToCalendarResource),
    [timelineFilters?.resources?.options],
  );

  const getResourceSectionValue = (/** @type {CalendarResource} */ resource) => {
    const section = resource.section ?? resource.extendedProps?.section ?? '';
    return section || 'Sans groupe';
  };

  const resourceOptionsGroups = useMemo(() => {
    /** @type {Record<string, CalendarResource[]>} */
    const groups = {};
    allResourceOptions.forEach((resource) => {
      const key = getResourceSectionValue(resource);
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(resource);
    });
    return Object.entries(groups).map(([lib, options]) => ({ lib, options }));
  }, [allResourceOptions]);

  const [draftResourceIds, setDraftResourceIds] = useState(/** @type {string[] | null} */ (null));

  const activeResourceIds = draftResourceIds ?? selectedResourceIds;

  const isDraftResourceSelected = (/** @type {string} */ resourceId) => (
    activeResourceIds.includes(resourceId)
  );

  const handleOpenResourcesModal = () => {
    setDraftResourceIds(selectedResourceIds);
    openResourcesModal();
  };

  const handleCloseResourcesModal = () => {
    setDraftResourceIds(null);
    closeResourcesModal();
  };

  const handleToggleDraftResource = (/** @type {string} */ resourceId) => {
    setDraftResourceIds((prev) => {
      const current = prev ?? selectedResourceIds;
      return current.includes(resourceId)
        ? current.filter((id) => id !== resourceId)
        : [...current, resourceId];
    });
  };

  const getDraftGroupVisibilityState = (/** @type {string} */ groupValue) => {
    const normalizedGroupValue = groupValue || 'Sans groupe';
    const groupResources = allResourceOptions.filter(
      (resource) => getResourceSectionValue(resource) === normalizedGroupValue,
    );
    if (groupResources.length === 0) return null;

    const visibleCount = groupResources.filter((resource) => isDraftResourceSelected(resource.id)).length;
    if (visibleCount === 0) return 'all-hidden';
    if (visibleCount === groupResources.length) return 'all-visible';
    return 'mixed';
  };

  const handleToggleDraftResourceGroup = (
    /** @type {string} */ groupValue,
    /** @type {string} */ groupField,
  ) => {
    const normalizedGroupValue = groupValue || 'Sans groupe';
    const groupResources = allResourceOptions.filter((resource) => {
      if (groupField !== 'section') return false;
      return getResourceSectionValue(resource) === normalizedGroupValue;
    });
    if (groupResources.length === 0) return;

    const allSelected = groupResources.every((resource) => isDraftResourceSelected(resource.id));
    setDraftResourceIds((prev) => {
      const current = prev ?? selectedResourceIds;
      if (allSelected) {
        const groupIds = new Set(groupResources.map((resource) => resource.id));
        return current.filter((id) => !groupIds.has(id));
      }
      const toAdd = groupResources
        .map((resource) => resource.id)
        .filter((id) => !current.includes(id));
      return [...current, ...toAdd];
    });
  };

  const handleApplyResourcesModal = () => {
    if (draftResourceIds) {
      postTimelineFiltersUpdate({ resourceIds: draftResourceIds });
    }
    setDraftResourceIds(null);
  };

  const { openFormPanel, handleOpenModal } = useFormPanelContext();

  const {
    popoverAddElementIsOpen,
    setPopoverAddElementIsOpen,
    searchValue: addElementSearchValue,
    setSearchValue: setAddElementSearchValue,
    addFormUrlsGrouped,
  } = useAddElement({ addFormUrls: calendarData.addFormUrls });

  const handleAddElementClick = () => {
    const urls = calendarData.addFormUrls;
    if (urls?.length === 1) {
      const withIds = addIdsToAddFormUrls(urls);
      handleOpenModal(withIds[0].url);
    } else {
      setPopoverAddElementIsOpen(!popoverAddElementIsOpen);
    }
  };

  const {
    handleEventClick,
    handleEventDidMount,
    handleEventWillUnmount,
    handleDateSelection,
    getResourceLabelData,
    handleEventResize,
    showRecurrenceWarningModal,
    handleEventDrop,
    cancelRecurrenceScope,
    confirmRecurrenceScope,
    focusedEvent,
    closeEventFocus,
  } = useFullCalendar(calendarData, {
    onOpenEventForm: (formUrl) => openFormPanel(formUrl),
    onFastEditEvent: (fastEditUrl, payload, eventId, { onError }) => {
      mutateFastEditEvent({ fastEditUrl, payload, eventId }, { onError });
    },
    onDuplicateEvent: (duplicateUrl) => {
      mutateDuplicateTimelineEvent({ duplicateUrl });
    },
  });

  const handleContextMenuSelect = (
    /** @type {import('../domain/fullCalendar/fullCalendarUseCase').TimelineEventContextMenuItem} */ item,
    /** @type {import('@fullcalendar/core').EventApi} */ event,
  ) => {
    switch (item.type) {
      case 'openFormModal':
        openFormPanel(item.url);
        break;
      case 'delete':
        mutateDeleteTimelineEvent({ deleteUrl: item.url, eventId: event.id });
        break;
      case 'duplicate':
        mutateDuplicateTimelineEvent({ duplicateUrl: item.url });
        break;
      case 'openTab':
        window.open(normalizeUrl(item.url), '_blank');
        break;
      default:
        break;
    }
  };

  const sidebarFilters = useCalendarSidebarFilters({
    filters: timelineFilters?.filters,
    filterValues: timelineFilters?.filterValues,
    isLoading: isTimelineFiltersLoading,
    isSubmitting: isTimelineFiltersMutating,
  });

  const handleSidebarFiltersMutation = (/** @type {Array<ApiManagerFilterValues>} */ data) => {
    postTimelineFiltersUpdate({ filterValues: data });
  };

  const activeFiltersCount = sidebarFilters.getSidebarFiltersCount(timelineFilters?.filterValues ?? []);

  const eventFocusLabels = useMemo(() => ({
    dateSameDay: t('fullCalendar.eventFocus.dateSameDay'),
    dateRange: t('fullCalendar.eventFocus.dateRange'),
    timeRange: t('fullCalendar.eventFocus.timeRange'),
    participant: t('fullCalendar.eventFocus.participant'),
    location: t('fullCalendar.eventFocus.location'),
    vehicle: t('fullCalendar.eventFocus.vehicle'),
    other: t('fullCalendar.eventFocus.other'),
    recurrence: t('fullCalendar.eventFocus.recurrence'),
  }), []);

  const eventFocusViewModel = useMemo(() => {
    if (!focusedEvent) {
      return null;
    }
    return buildEventFocusViewModel(focusedEvent, eventFocusLabels);
  }, [eventFocusLabels, focusedEvent]);

  useEffect(() => {
    const viewName = viewConfig.fcView;
    headerCalendarRef.current?.getApi()?.changeView(viewName, viewConfig.duration);
    calendarRefs.current.forEach((calendarRef) => {
      calendarRef?.getApi()?.changeView(viewName, viewConfig.duration);
    });
  }, [viewConfig.duration, viewConfig.fcView, calendarData.viewType]);

  const resourceLabelContentWrapper = (/** @type {import('@fullcalendar/resource').ResourceLabelContentArg} */ arg) => {
    const { duration, amplitude } = getResourceLabelData(
      arg.resource.id,
      arg.view.type,
      arg.view.currentStart,
      arg.view.currentEnd,
    );

    return renderCalendarResourceLabel({
      title: arg.resource.title,
      duration,
      amplitude,
    });
  };

  const resourceLabelDidMount = (/** @type {import('@fullcalendar/resource').ResourceLabelMountArg} */ arg) => {
    const backgroundColor = arg.resource.extendedProps?.backgroundColor;
    if (!backgroundColor) {
      return;
    }
    arg.el.classList.add(styles['resource-color-stripe']);
    arg.el.style.setProperty('--resource-accent', backgroundColor);
  };

  const splitDays = useMemo(() => {
    if (viewConfig.splitBy !== 'day' || !calendarData.dateStart || !calendarData.dateEnd) {
      return [];
    }

    return eachDayOfInterval({
      start: parseISO(calendarData.dateStart),
      end: parseISO(calendarData.dateEnd),
    });
  }, [calendarData.dateEnd, calendarData.dateStart, viewConfig.splitBy]);

  const calendarSplits = useMemo(() => {
    if (viewConfig.splitBy !== 'week' && viewConfig.splitBy !== 'month') {
      return [];
    }
    return buildCalendarSplits(
      calendarData.dateStart,
      calendarData.dateEnd,
      viewConfig.splitBy,
    );
  }, [calendarData.dateEnd, calendarData.dateStart, viewConfig.splitBy]);

  const stackedDayColumnSlots = viewConfig.dayColumns
    ? {
      slotMinTime: DAY_COLUMN_SLOT_MIN_TIME,
      slotMaxTime: DAY_COLUMN_SLOT_MAX_TIME,
      slotDuration: DAY_COLUMN_SLOT_DURATION,
    }
    : {
      slotMinTime,
      slotMaxTime,
      slotDuration,
    };

  const scrollerElements = useScrollerElements('.fc-scroller', [
    calendarData.viewType,
    periodType,
    splitDays.length,
    calendarSplits.length,
  ]);
  useSyncHorizontalScroll(scrollerElements);

  if (isTimelineFiltersLoading || isTimelineLoading) {
    return (
      <TimelineSkeleton
        stickyHeight={stickyHeight}
        remainingHeight={remainingHeight}
        isSidebarCollapsed={isSidebarCollapsed}
      />
    );
  }

  const initialDate = calendarData.dateStart || timelineFilters?.dateRange.rangeStartDate;
  const showOrientationSwitch = shouldShowTimelineOrientationSwitch(timelineFilters?.direction);

  return (
    <div
      ref={layoutRef}
      className={cn([
        styles.layout,
        isSidebarCollapsed && styles['layout-sidebar-collapsed'],
      ])}
      style={/** @type {import('react').CSSProperties} */ ({
        '--sidebar-sticky-top': `${stickyHeight}px`,
        '--sidebar-remaining-height': `${remainingHeight}px`,
      })}
    >
      <div className={styles['sidebar-column']}>
        <FilterCalendarSidebar
          orientation={showOrientationSwitch ? direction : undefined}
          onOrientationChange={showOrientationSwitch ? handleOrientationChange : undefined}
          resourcesGroups={resourcesGroups}
          isResourceVisible={isResourceVisible}
          onToggleResource={toggleResourceVisibility}
          showResourcesModal={showResourcesModal}
          onOpenResourcesModal={handleOpenResourcesModal}
          onCloseResourcesModal={handleCloseResourcesModal}
          onApplyResourcesModal={handleApplyResourcesModal}
          allResources={allResourceOptions}
          resourceGroupField={resourceGroupField}
          getGroupVisibilityState={getGroupVisibilityState}
          onToggleGroup={toggleGroupVisibility}
          popoverResourcesGroups={resourceOptionsGroups}
          popoverIsResourceVisible={isDraftResourceSelected}
          popoverOnToggleResource={handleToggleDraftResource}
          popoverGetGroupVisibilityState={getDraftGroupVisibilityState}
          popoverOnToggleGroup={handleToggleDraftResourceGroup}
          popoverResourceGroupField="section"
          searchResources={searchResources}
          setSearchResources={setSearchResources}
          sidebarFilters={sidebarFilters}
          onSidebarFiltersSubmit={handleSidebarFiltersMutation}
          isSidebarFiltersLoading={isTimelineFiltersLoading}
          isSidebarFiltersSubmitting={isTimelineFiltersMutating}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={onToggleSidebarCollapse}
          activeFiltersCount={activeFiltersCount}
          visibleResourcesCount={visibleResources.length}
        />
      </div>
      <div className={styles.content}>
        <div className={styles['content-header']}>
          {!isInTab ? <TableTitle title={timelineFilters?.title ?? 'Planning'} /> : null}
          <CalendarHeader
            headerFilters={timelineFilters?.headerFilters ?? []}
            onStartTimeChange={handleStartTimeChange}
            onEndTimeChange={handleEndTimeChange}
            startTime={startTime}
            endTime={endTime}
            scope={periodType}
            ranges={ranges}
            template="timeline"
            dateRange={dateRange}
            handleDateRangeMutation={handleDateRangeMutation}
            handlePrevNextValueMutation={handlePrevNextValueMutation}
            handleTypeValueChangeMutation={handleTypeValueChangeMutation}
          />
        </div>
        <div className={styles['content-scroll']}>
          {showStickyMasterHeader ? (
            <div
              className={`${styles['calendar-scroll']} ${styles['calendar-scroll-sticky']}`}
              ref={firstScrollDivRef}
            >
              <Calendar
                ref={headerCalendarRef}
                stickyHeaderDates={false}
                key={`${calendarData.viewType}-${periodType}-header`}
                events={null}
                resources={[]}
                resourceAreaColumns={calendarData.resourcesAreaColumns}
                slotMinTime={stackedDayColumnSlots.slotMinTime}
                slotMaxTime={stackedDayColumnSlots.slotMaxTime}
                slotDuration={stackedDayColumnSlots.slotDuration}
                initialDate={initialDate}
                duration={viewConfig.duration}
                initialView={viewConfig.fcView}
                hideHorizontalScrollBar
                headerToolbar={null}
                stickyTop={null}
                hourHeaders={showHourHeaders}
                dayColumns={viewConfig.dayColumns}
              />
            </div>
          ) : null}
          <div
            className={styles['calendar-scroll']}
            ref={secondScrollDivRef}
          >
            {viewConfig.splitBy === 'day'
              ? (
                splitDays.map((date, index) => (
                  <Calendar
                    stickyHeaderDates={false}
                    displayDayHeader={false}
                    className="margin-top-m"
                    key={date.toISOString()}
                    events={calendarData.events}
                    resources={visibleResources}
                    resourceAreaColumns={calendarData.resourcesAreaColumns}
                    eventClick={handleEventClick}
                    slotMinTime={slotMinTime}
                    slotMaxTime={slotMaxTime}
                    slotDuration={slotDuration}
                    eventContent={renderCalendarEvent}
                    eventDidMount={handleEventDidMount}
                    eventWillUnmount={handleEventWillUnmount}
                    onSelect={handleDateSelection}
                    onEventResize={handleEventResize}
                    eventDrop={handleEventDrop}
                    resourceLabelContent={resourceLabelContentWrapper}
                    ref={(el) => {
                      calendarRefs.current[index] = el;
                    }}
                    hideHorizontalScrollBar
                    initialDate={date.toISOString()}
                    duration={{ days: 1 }}
                    initialView={viewConfig.fcView}
                    headerToolbar={{
                      start: 'title',
                      center: '',
                      end: '',
                    }}
                    hideHeader
                    hourHeaders={showHourHeaders}
                  />
                ))
              ) : null}
            {calendarSplits.length > 0
              ? (
                calendarSplits.map((split, index) => (
                  <Calendar
                    key={split.start.toISOString()}
                    stickyHeaderDates={false}
                    displayDayHeader={false}
                    className="margin-top-m"
                    dayColumns
                    blockTitle={split.title}
                    events={calendarData.events}
                    resources={visibleResources}
                    resourceAreaColumns={calendarData.resourcesAreaColumns}
                    eventClick={handleEventClick}
                    slotMinTime={stackedDayColumnSlots.slotMinTime}
                    slotMaxTime={stackedDayColumnSlots.slotMaxTime}
                    slotDuration={stackedDayColumnSlots.slotDuration}
                    eventContent={renderCalendarEvent}
                    eventDidMount={handleEventDidMount}
                    eventWillUnmount={handleEventWillUnmount}
                    onSelect={handleDateSelection}
                    onEventResize={handleEventResize}
                    eventDrop={handleEventDrop}
                    resourceLabelContent={resourceLabelContentWrapper}
                    resourceLabelDidMount={resourceLabelDidMount}
                    ref={(el) => {
                      calendarRefs.current[index] = el;
                    }}
                    hideHorizontalScrollBar
                    initialDate={split.start.toISOString()}
                    duration={viewConfig.duration}
                    initialView={viewConfig.fcView}
                    headerToolbar={{
                      start: 'title',
                      center: '',
                      end: '',
                    }}
                  />
                ))
              ) : null}
            {!viewConfig.splitBy
              ? (
                <Calendar
                  className="margin-top-m"
                  ref={(el) => {
                    calendarRefs.current[0] = el;
                  }}
                  events={calendarData.events}
                  resources={visibleResources}
                  resourceAreaColumns={calendarData.resourcesAreaColumns}
                  eventClick={handleEventClick}
                  slotMinTime={slotMinTime}
                  slotMaxTime={slotMaxTime}
                  slotDuration={slotDuration}
                  eventContent={renderCalendarEvent}
                  eventDidMount={handleEventDidMount}
                  eventWillUnmount={handleEventWillUnmount}
                  onSelect={handleDateSelection}
                  onEventResize={handleEventResize}
                  eventDrop={handleEventDrop}
                  initialDate={initialDate}
                  duration={viewConfig.duration}
                  initialView={viewConfig.fcView}
                  hideHorizontalScrollBar
                  resourceLabelContent={resourceLabelContentWrapper}
                  stickyHeaderDates={false}
                  displayDayHeader={isAgendaView}
                  headerToolbar={{
                    start: 'title',
                    center: '',
                    end: '',
                  }}
                  hideHeader={!isAgendaView}
                  hourHeaders={showHourHeaders}
                />
              ) : null}
          </div>
        </div>
        <EventContextMenu onSelect={handleContextMenuSelect} />
        <DateSelectionModal
          addFormUrls={calendarData.addFormUrls}
          onSelect={(formUrl) => openFormPanel(formUrl)}
        />
        <Modal
          isOpen={!!eventFocusViewModel}
          onClose={closeEventFocus}
          className={styles['event-focus-dialog']}
        >
          {eventFocusViewModel ? (
            <CalendarEventFocus viewModel={eventFocusViewModel} />
          ) : null}
        </Modal>
        <Modal
          isOpen={showRecurrenceWarningModal}
          onClose={cancelRecurrenceScope}
          className={modalStyles['modal-panel-large']}
          header={(
            <ModalHeader
              title={t('fullCalendar.modals.recurrenceWarning.title')}
              onClose={cancelRecurrenceScope}
            />
          )}
        >
          <p style={{ color: 'var(--neutral-900' }}>{t('fullCalendar.modals.recurrenceWarning.content')}</p>
          <p style={{ color: 'var(--neutral-700' }}>{t('fullCalendar.modals.recurrenceWarning.subContent')}</p>
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', flexWrap: 'wrap',
          }}
          >
            <Button
              onClick={cancelRecurrenceScope}
              color="no-background"
            >
              <p>{t('fullCalendar.modals.recurrenceWarning.cancel')}</p>
            </Button>
            <Button
              onClick={() => confirmRecurrenceScope('single')}
              color="primary100"
            >
              <p>{t('fullCalendar.modals.recurrenceWarning.onlyThisEvent')}</p>
            </Button>
            <Button
              onClick={() => confirmRecurrenceScope('next')}
              color="primary100"
            >
              <p>{t('fullCalendar.modals.recurrenceWarning.thisAndFollowing')}</p>
            </Button>
            <Button
              onClick={() => confirmRecurrenceScope('all')}
            >
              <p>{t('fullCalendar.modals.recurrenceWarning.allEvents')}</p>
            </Button>
          </div>
        </Modal>
        <FloatingMenu>
          <FloatingMenuGroups>
            <FloatingMenuGroup>
              <ResponsiveActionButton
                color="no-background"
                size="small"
                iconPosition="right"
                icon="export"
                ariaLabel={t('table.export')}
                label={t('table.export')}
                onClick={() => {}}
              />
              {calendarData.addFormUrls?.length ? (
                <Popover
                  open={popoverAddElementIsOpen}
                  onOpenChange={setPopoverAddElementIsOpen}
                  placement="top"
                >
                  <PopoverTrigger>
                    <ResponsiveActionButton
                      color="primary"
                      size="small"
                      icon="add"
                      iconPosition="left"
                      desktopPresentation="labelOnly"
                      ariaLabel={t('table.addElement')}
                      label={t('table.addElement')}
                      onClick={handleAddElementClick}
                    />
                  </PopoverTrigger>
                  <PopoverContent>
                    <CustomVueListGroup>
                      <CustomVueListGroupList>
                        <CustomVueListGroupTitle>Ajouter un élément</CustomVueListGroupTitle>
                        <ColumnsVisibility
                          search={(
                            <SearchField
                              id="timeline-add-element-search"
                              placeholder={t('table.filters.columnsGroupsSearch')}
                              value={addElementSearchValue}
                              onChange={(e) => setAddElementSearchValue(e.target.value)}
                              onFocus={() => {}}
                              className="margin-top-s"
                            />
                          )}
                          options={addFormUrlsGrouped}
                          messageNoOptions={t('table.filters.noResult')}
                        >
                          {(key, items) => (
                            <SearchGroupList
                              key={key}
                              hasLabel={!!key}
                              items={items}
                              label={key}
                            >
                              {(/** @type {AddFormUrlWithId} */ item) => (
                                <SearchListItem
                                  key={item.id}
                                  id={item.id}
                                  theme="group"
                                  onClick={() => {
                                    handleOpenModal(item.url);
                                    setPopoverAddElementIsOpen(false);
                                  }}
                                  isSelected={false}
                                >
                                  <LabelWithIconAndColor
                                    label={item.lib}
                                    icon={item.icon}
                                    iconColor={item.iconColor}
                                    hasColorSquareFallback
                                  />
                                </SearchListItem>
                              )}
                            </SearchGroupList>
                          )}
                        </ColumnsVisibility>
                      </CustomVueListGroupList>
                    </CustomVueListGroup>
                  </PopoverContent>
                </Popover>
              ) : null}
            </FloatingMenuGroup>
          </FloatingMenuGroups>
        </FloatingMenu>
      </div>
    </div>
  );
}

export default Timeline;
